import { BoundSelectField, BoundSelectFieldProps } from "@homebound/beam";
import { useCallback, useMemo, useState } from "react";
import { MaterialVariantsFilter } from "src/generated/graphqlTypes";
import {
  AddEditItemModal_MvFragment,
  useAddEditItemModal_GetMaterialVariantsQuery,
} from "src/routes/libraries/plan-package/takeoffs/components/AddItems/AddEditItemModal.generated";

export type MaterialVariantSelectFieldProps = Omit<
  BoundSelectFieldProps<AddEditItemModal_MvFragment, string>,
  "options"
> & {
  //* * Default filter to be used when pulling variants */
  filter: MaterialVariantsFilter;
  initialOptions: AddEditItemModal_MvFragment[];
};

/** Material Variant select field that load options based on user's search
 * This component loads Material Variants and it's MAVs
 */
export function MaterialVariantSelectField({
  filter,
  initialOptions,
  onSelect,
  ...props
}: MaterialVariantSelectFieldProps) {
  const [search, setSearch] = useState<string>();
  const [selectedOption, setSelectedOption] = useState<AddEditItemModal_MvFragment>();
  const { data } = useAddEditItemModal_GetMaterialVariantsQuery({
    // Bumping the limit to 200, to where users can search materials variants without any attributes, and it will be returned in one page.
    // I.e. we have ~120 interior doors that all share the same code
    variables: { filter: { ...filter, search }, page: { limit: 200 } },
    fetchPolicy: "cache-first",
    // Skip until the user types at least 2 characters to avoid expensive unfiltered queries
    skip: (search ?? "").length < 2,
  });

  const handleSelect = useCallback(
    (id: string | undefined, option: AddEditItemModal_MvFragment | undefined) => {
      setSelectedOption(option);
      onSelect?.(id, option);
    },
    [onSelect],
  );

  const options = useMemo(
    () =>
      [
        ...(selectedOption ? [selectedOption] : []),
        ...initialOptions,
        ...(data?.materialVariants.entities ?? []),
      ].uniqueByKey("id"),
    [data, initialOptions, selectedOption],
  );

  return (
    <BoundSelectField
      placeholder="Type at least 2 characters to search"
      {...props}
      options={options}
      onSearch={setSearch}
      onSelect={handleSelect}
    />
  );
}
